import axios from 'axios';
import { Reward, Rewards } from '@lib/entities';
import client, { toUrlParams, wrapApiCall, handleAxiosError, ApiError } from './client';
import { LoyaltyResponse, RewardItem } from '@lib/state/Rewards';
import { AppleWalletPassResponse } from '@lib/types/appleWallet';
import { Challenge } from '@lib/state/Rewards';

export const fetchRewards = wrapApiCall(async (lat: number, lng: number): Promise<Reward[]> => {
    const params = toUrlParams({ lat, lng });
    // TODO: wait until the new rewards endpoint is sorted and do this with a zod schema
    const response = await client.get(`v1/Loyalty/rewards?${params}`);
    const entries = response.data || [];

    return entries.flatMap((entry: { reward: Rewards.RewardPayload }) => {
        const rewardOrError = Rewards.payloadToReward(entry.reward);

        if (Rewards.isInvalidPayloadError(rewardOrError)) {
            console.error(rewardOrError);
            return [];
        }
        return [rewardOrError];
    });
});

export const fetchLoyalty = wrapApiCall(
    async (forceFailedFetchLoyalty: boolean): Promise<LoyaltyResponse> => {
        if (forceFailedFetchLoyalty) {
            const response = await client.get('v2/loyalty-testing');
            return response.data;
        }

        const response = await client.get('v2/loyalty/');
        return response.data;
    }
);

export type OpenMysteryBowlSuccessResponse = {
    mystery_bowl_content?: RewardItem;
    finished_progress_reward?: RewardItem | null;
};

export type OpenMysteryBowlResponse = { error: boolean } | OpenMysteryBowlSuccessResponse;

export const openMysteryBowl = async (id: string): Promise<OpenMysteryBowlResponse | ApiError> => {
    try {
        const response = await client.post(`v2/loyalty/mystery-bowl/${id}/open`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

export type LoyaltyCountersResponse = {
    earned_rewards_counter?: number;
    mystery_bowls?: string[];
    has_unseen_completed_challenge?: boolean;
};

export const fetchLoyaltyCounters = wrapApiCall(async (): Promise<LoyaltyCountersResponse> => {
    const response = await client.get('v2/loyalty/counters');
    return response.data;
});

export const generateAppleWalletPass = async (): Promise<AppleWalletPassResponse | ApiError> => {
    try {
        const response = await client.get('v1/wallet/loyalty-pass');
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

export type OptInToChallengeResponse = { error: boolean } | { challenge: Challenge };

export const optInToChallenge = async (
    challengeId: string
): Promise<OptInToChallengeResponse | ApiError> => {
    try {
        const response = await client.post(`v2/loyalty/challenges/${challengeId}/opt-in`);

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }

            return handleAxiosError(error);
        }

        throw error;
    }
};

export const markChallengeModalSeen = wrapApiCall(async (): Promise<void> => {
    await client.post('v2/loyalty/challenges/modal-seen');
});

export default {
    fetchRewards,
    fetchLoyalty,
    openMysteryBowl,
    fetchLoyaltyCounters,
    generateAppleWalletPass,
    optInToChallenge,
    markChallengeModalSeen
};
